import { useContext, useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import type {
  DictationCompleteEvent,
  MhFormContext,
  TextboxFormBuilderField,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import type { FieldProps } from "../../pages/FieldProps.js";
import { FormLabel } from "../design-system/FormLabel.js";
import {
  Button,
  PopupDialog,
  ReadOnlyContent,
} from "../design-system/index.js";
import { TextField } from "../design-system/TextField/TextField.js";
import {
  TortusDictationButton,
  useOptionalTortusDictation,
  type DictationInsertMode,
} from "../design-system/TortusAvtModal/index.js";

function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text);
}

function CopyFromModal({
  field,
  fieldProps,
  onClose,
}: {
  field: TextboxFormBuilderField<any>;
  fieldProps: FieldProps<any>;
  onClose: () => void;
}) {
  const copyValue = field.copyFrom
    ?.valueFn(fieldProps.values, fieldProps.context as MhFormContext)
    ?.trim();

  const valueIsEmpty = !copyValue || copyValue.length === 0;

  return (
    <PopupDialog open onClose={onClose}>
      <DialogTitle>{field.copyFrom?.label}</DialogTitle>
      {!valueIsEmpty ? (
        <ReadOnlyContent content={copyValue.split("\n")} />
      ) : (
        <Typography>Nothing to copy</Typography>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Box>
          <Button
            variant="outlined"
            color="inherit"
            label="Close"
            onClick={onClose}
          />
        </Box>
        <Box sx={{ display: "flex", gap: "1rem" }}>
          <Button
            label="Copy to Clipboard"
            disabled={valueIsEmpty}
            onClick={() => {
              const runAsync = async () => {
                await copyToClipboard(copyValue!);
                onClose();
              };

              void runAsync();
            }}
          />
          <Button
            label="Insert at Start"
            disabled={valueIsEmpty}
            onClick={() => {
              const newValue = [copyValue, fieldProps.values[field.field]]
                .filter(Boolean)
                .join("\n\n");

              fieldProps.setValues({
                ...fieldProps.values,
                [field.field]: newValue,
              });
              onClose();
            }}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}

export type TextboxFormFieldProps<Data extends { [k: string]: any }> = {
  field: TextboxFormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
  autoFocus?: boolean;
};

export function TextboxFormField<Data extends { [k: string]: any }>({
  field,
  fieldProps,
  autoFocus,
}: TextboxFormFieldProps<Data>) {
  const isError = !!(
    fieldProps.touched[field.field] && fieldProps.errors[field.field]
  );
  const value = fieldProps.values[field.field] as string;
  const theme = useTheme();

  const [showCopyFromModal, setShowCopyFromModal] = useState(false);
  const dictation = useOptionalTortusDictation();

  const userContext = useContext(LoggedInUserContext);
  const orgConfig = userContext?.user?.sessionOrganisationConfiguration;
  // Dictation needs somewhere to dock, so it is only offered on pages that host
  // the panel — not on the other pages that reuse these fields.
  const avtDictationAvailable =
    dictation !== null &&
    !!field.enableAvtDictation &&
    !!orgConfig?.tortusAvtDictationEnabled;
  const hasFieldActions = !!field.copyFrom || avtDictationAvailable;
  const labelOutsideTextField = hasFieldActions || !!field.guidanceAboveInput;

  // formDraftId comes from the route (it isn't on the form context).
  // Patient comes from MhFormContext, which the form-draft pages
  // populate from the already-loaded ExtendedPatient.
  const { formDraftId } = useParams<{ formDraftId?: string }>();
  const mhContext = fieldProps.context as MhFormContext | undefined;
  const formPatient = mhContext?.patient;

  const currentFieldText = (fieldProps.values[field.field] as string) ?? "";

  // Dictation hands its result back through a callback captured when the user
  // launched it, and the form stays editable throughout — so by the time it
  // fires, the values from that render can be many edits out of date. Writing
  // them back would silently discard everything typed meanwhile, so read the
  // latest through a ref.
  //
  // Not the functional `setValues(prev => …)` form, which would be neater: the
  // prop is typed to accept an updater, but the implementations behind it take a
  // plain object and would store the function itself.
  const latestFieldProps = useRef(fieldProps);
  useEffect(() => {
    latestFieldProps.current = fieldProps;
  });

  const insertIntoField = (text: string, insertMode: DictationInsertMode) => {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return;
    }
    const { values, setValues } = latestFieldProps.current;
    const previous = ((values[field.field] as string) ?? "").trim();
    const next =
      insertMode === "overwrite" || previous.length === 0
        ? trimmed
        : `${previous}\n\n${trimmed}`;
    setValues({
      ...values,
      [field.field]: next,
    });
  };

  const handleDictationComplete = (
    event: DictationCompleteEvent,
    insertMode: DictationInsertMode,
  ) => {
    insertIntoField(event.chosen, insertMode);
  };

  return (
    <Box data-testid={`field:${field.field}`}>
      {field.checkboxToShowField && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={fieldProps.values[field.checkboxToShowField]}
                onChange={() => {
                  fieldProps.setValues({
                    ...fieldProps.values,
                    [field.field]: null,
                    [field.checkboxToShowField!]:
                      !fieldProps.values[field.checkboxToShowField!],
                  });
                }}
              />
            }
            label={
              <Typography
                data-testid="label-id"
                css={css`
                  font-weight: bold;
                  color: ${
                    isError
                      ? theme.palette.error.main
                      : theme.palette.text.primary
                  };
                `}
              >
                {field.label}
              </Typography>
            }
          />
        </Box>
      )}
      {/*
         When the field has guidance above the input or action buttons (Copy From /
         Launch dictation), render the label manually here so they can sit
         *between* the label and the input. Otherwise the label stays inside
         <TextField>. The `checkboxToShowField` branch renders its own label
         above.
       */}
      {!field.checkboxToShowField && labelOutsideTextField && field.label && (
        <FormLabel label={field.label} error={isError} />
      )}
      {field.guidanceAboveInput && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {field.guidanceAboveInput}
        </Typography>
      )}
      {hasFieldActions && (
        <Box
          sx={{
            mb: "1rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {field.copyFrom && (
            <Button
              label={field.copyFrom.label}
              onClick={() => {
                setShowCopyFromModal(true);
              }}
            />
          )}
          {showCopyFromModal && (
            <CopyFromModal
              field={field}
              fieldProps={fieldProps}
              onClose={() => setShowCopyFromModal(false)}
            />
          )}
          {avtDictationAvailable && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TortusDictationButton
                testId={`tortusDictate:${field.field}`}
                onClick={() => {
                  dictation?.requestDictation({
                    patient: formPatient,
                    formDraftId,
                    existingText: currentFieldText,
                    onDictationComplete: handleDictationComplete,
                  });
                }}
              />
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      How dictation works
                    </Typography>
                    <Typography variant="body2">
                      Click <em>Launch dictation</em> to open Tortus AVT. Speak
                      your clinical reasoning aloud — when you finish and
                      approve the transcript, it will be appended to this field.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      You can ignore the &ldquo;letter&rdquo; and other output
                      options shown in Tortus; only the dictation transcript is
                      used here.
                    </Typography>
                  </Box>
                }
              >
                <IconButton
                  size="small"
                  aria-label="How dictation works"
                  data-testid={`tortusDictateHelp:${field.field}`}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}
      <Box>
        {(!field.checkboxToShowField ||
          fieldProps.values[field.checkboxToShowField!]) && (
          <>
            <TextField
              inputProps={{
                "data-1p-ignore": true,
                ...(field.numeric
                  ? {
                      type: "number",
                      inputMode: "numeric",
                      min: field.min,
                      max: field.max,
                    }
                  : {}),
              }}
              InputProps={
                field.suffix
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          {field.suffix}
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              narrow={field.narrow}
              placeholder={field.placeholder}
              disabled={field.disabled}
              autoFocus={autoFocus ?? false}
              label={
                field.checkboxToShowField || labelOutsideTextField
                  ? ""
                  : field.label
              }
              errorMessage={
                isError ? (fieldProps.errors[field.field] as string) : undefined
              }
              showHelperText={isError || !!field.afterLabel}
              subtext={field.afterLabel}
              rows={field.rows}
              multiline={!!field.rows}
              useFullWidth={true}
              name={field.field}
              onChange={(value: string) => {
                fieldProps.setValues({
                  ...fieldProps.values,
                  [field.field]: field.numeric
                    ? value.replace(/[^0-9]/g, "")
                    : value,
                });
              }}
              value={value || ""}
              onBlur={fieldProps.handleBlur}
            />
          </>
        )}
      </Box>
    </Box>
  );
}
